import styles from "../add.module.css";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { useState, useEffect } from "react";
import MapComponent from "../../../../frontend/components/Map";
import { TextField } from "@mui/material";
import { Plus, Trash2, MapPin } from "react-feather";
import ManualLocationDialog from "../../../../admin/components/popups/Manuallocationdialog";

function Step3({ saveStep3Data, backToTab, bookingData, selectCountry }) {
    const [mapCenter, setMapCenter] = useState({ lat: 5.614818, lng: -0.205874 });
    const [isMarker, setIsMarker] = useState(false);
    const [zoom, setZoom] = useState(12);

    // Popup for addresses Google Places can't find: user drags a pin on a
    // map, we reverse-geocode it into a suggested address.
    const [manualDialogOpen, setManualDialogOpen] = useState(false);

    const { register, handleSubmit, control, formState: { errors }, clearErrors, setValue } = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            address: "",
            alt_phones: [],
        },
    });

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name: "alt_phones"
    });

    const { ref: placeRef1 } = usePlacesWidget({
        apiKey: process.env.REACT_APP_MAP_API_KEY,
        onPlaceSelected: (place) => {
            setValue("address", place.formatted_address);
            setValue("latitude", place.geometry.location.lat());
            setValue("longitude", place.geometry.location.lng());

            setMapCenter({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });
            setIsMarker(true);
            setZoom(18);
        },
        options: {
            types: ["geocode"],
            componentRestrictions: { country: selectCountry }
        },
    });

    // Called when the user confirms a pin in the manual-location popup.
    // Writes the picked address/coordinates straight into the form, same
    // as if Places autocomplete had returned them.
    const handleManualLocationConfirm = (data) => {
        setValue("address", data.address, { shouldValidate: true });
        setValue("latitude", data.latitude);
        setValue("longitude", data.longitude);
        setMapCenter({ lat: data.latitude, lng: data.longitude });
        setIsMarker(true);
        setZoom(17);
        clearErrors(["address"]);
        setManualDialogOpen(false);
    };

    // Guest flow: no saved-address list to fetch, just prefill from bookingData
    // if the person navigates back to this step.
    useEffect(() => {
        setValue("name", bookingData?.recipientData?.name);
        setValue("email", bookingData?.recipientData?.email);
        setValue("phone", bookingData?.recipientData?.phone);
        setValue("address", bookingData?.recipientData?.address);

        if (bookingData?.recipientData?.alt_phones?.length) {
            replace(bookingData?.recipientData?.alt_phones);
        }

        if (bookingData?.recipientData?.latitude && bookingData?.recipientData?.longitude) {
            setValue("latitude", bookingData?.recipientData?.latitude);
            setValue("longitude", bookingData?.recipientData?.longitude);
            setMapCenter({ lat: parseFloat(bookingData?.recipientData?.latitude), lng: parseFloat(bookingData?.recipientData?.longitude) });
            setIsMarker(true);
            setZoom(18);
        }
    }, [bookingData, setValue, replace]);

    const onSubmit = (data) => {
        document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
        saveStep3Data(data);
    }

    return (<>
        <div className={`${styles.StepOneForm}`}>
            <p className={styles.SectionLabel}>Recipient / delivery details</p>
            <form id="step2-form" onSubmit={handleSubmit(onSubmit)}>
                <div className={`${styles.StepOneFormRow}`}>
                    <div className={`${styles.StepOneFormBig}`}>
                        <div className={styles.FormFullGroup}>
                            <Controller
                                control={control}
                                {...register("name", { required: "This field is Required" })}
                                render={({ field }) => (<TextField {...field} label="Name*" variant="outlined" />)}
                            />
                            {errors.name && (<span className={styles.ErrorM}>{errors?.name?.message}</span>)}
                        </div>

                        <div className={styles.FormFullGroup}>
                            <Controller
                                control={control}
                                {...register("phone", { required: "This field is Required" })}
                                render={({ field }) => (<TextField {...field} label="Phone*" variant="outlined" />)}
                            />
                            {errors.phone && (<span className={styles.ErrorM}>{errors.phone.message}</span>)}
                        </div>

                        {fields.map((item, index) => {
                            return (<div className={styles.FormFullGroup} key={item.id}>
                                <div className={`${styles.AltConSec}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Controller
                                        control={control}
                                        {...register(`alt_phones[${index}].value`, { required: "This field is Required" })}
                                        render={({ field }) => (<TextField {...field} label="Alt Phone*" variant="outlined" />)}
                                    />
                                    <button type='button' onClick={() => remove(index)} className={`${styles.ConNoBU}`}><Trash2 /></button>
                                </div>
                                {(errors?.alt_phones?.length > 0 && errors?.alt_phones[index]?.value) && <p className={styles.ErrorM}>{errors?.alt_phones[index]?.value?.message}</p>}
                            </div>)
                        })}
                        <button type='button' onClick={() => { append({ value: '' }); }} className={`${styles.AddAltConNO}`}><Plus /> Add alt phone</button>

                        <div className={styles.FormFullGroup}>
                            <Controller
                                control={control}
                                {...register("email")}
                                render={({ field }) => (<TextField {...field} label="Email" variant="outlined" />)}
                            />
                        </div>
                    </div>

                    <div className={`${styles.StepOneFormSmall2} ${styles.StepOneFormSmall254}`}>
                        <div className={styles.FormFullGroup2}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <Controller
                                    control={control}
                                    {...register("address", { required: "This field is Required" })}
                                    render={({ field }) => (<TextField {...field} label="Address*" variant="outlined" inputRef={placeRef1} placeholder="" />)}
                                />
                                <button
                                    type="button"
                                    aria-label="Can't find this address? Select on map"
                                    className={styles.ConNoBU}
                                    onClick={() => setManualDialogOpen(true)}
                                    title="Can't find this address? Select on map"
                                >
                                    <MapPin />
                                </button>
                            </div>
                            {errors?.address && (<p className={styles.ErrorM}>{errors?.address?.message}</p>)}
                            <p className={styles.FieldHint} style={{ marginTop: 6 }}>
                                Can't find the address above?{" "}
                                <span
                                    role="button"
                                    tabIndex={0}
                                    style={{ color: "var(--accent)", fontWeight: 600, cursor: "pointer" }}
                                    onClick={() => setManualDialogOpen(true)}
                                >
                                    Select the location on the map instead
                                </span>.
                            </p>
                        </div>
                        <div>
                            <MapComponent center={mapCenter} zoom={zoom} isMarker={isMarker} />
                        </div>
                    </div>

                    <div className={`${styles.StepBUSec}`}>
                        <button type="button" onClick={backToTab} className={`${styles.Previous}`}>Back</button>
                        <button type="submit" className={`${styles.Next}`}>Next</button>
                    </div>
                </div>
            </form>
        </div>

        <ManualLocationDialog
            open={manualDialogOpen}
            onClose={() => setManualDialogOpen(false)}
            onConfirm={handleManualLocationConfirm}
            initialCenter={isMarker ? mapCenter : null}
            showPostCode={false}
        />
    </>);
}

export default Step3;